import React, { useCallback, useRef, useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { type IssueKey, toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { keyboardShortcuts_issueNavigator$key } from '@atlassian/jira-relay/src/__generated__/keyboardShortcuts_issueNavigator.graphql';
import { useOrderByOpenState } from '../../../../../controllers/order-by-open-state/index.tsx';
import {
	useSelectedIssueKey,
	useNextIssueKey,
	usePreviousIssueKey,
	useSelectedIssueActions,
} from '../../../../../controllers/selected-issue/hooks.tsx';

import messages from './messages.tsx';

type Props = {
	disableListControls?: boolean;
	onNavigateToIssue: (issueKey: string) => void;
	onIssueChangeEvent: (issueKey: IssueKey) => void;
	focusedIssueKey: IssueKey | null;
	focusedIssue: keyboardShortcuts_issueNavigator$key | null;
};

const useUpdatingRef = <T,>(
	value: T,
): {
	current: T;
} => {
	const ref = useRef<T>(value);
	ref.current = value;
	return ref;
};

const KeyboardShortcuts = (props: Props) => {
	const issueData = useFragment<keyboardShortcuts_issueNavigator$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment keyboardShortcuts_issueNavigator on JiraIssue {
				issueTypeField {
					issueType {
						name
					}
				}
			}
		`,
		props.focusedIssue,
	);

	const workType = issueData?.issueTypeField?.issueType?.name;

	const { formatMessage } = useIntl();
	const propsRef = useUpdatingRef(props);
	const openStateRef = useUpdatingRef(useOrderByOpenState());
	const selectedIssueKey = useSelectedIssueKey();
	const selectedIssueKeyRef = useUpdatingRef(selectedIssueKey);

	const { selectNextIssue, selectPreviousIssue } = useSelectedIssueActions();

	const nextIssueKey = useNextIssueKey() ?? toIssueKey('');

	const nextIssueKeyRef = useRef(nextIssueKey);
	nextIssueKeyRef.current = nextIssueKey;

	const handleNext = useCallback(() => {
		const { onIssueChangeEvent } = propsRef.current;
		if (selectNextIssue(true)) {
			onIssueChangeEvent && onIssueChangeEvent(nextIssueKeyRef.current);
		}
		// Dependencies must be stable as downstream useMemo has an empty dependencies array
	}, [propsRef, selectNextIssue]);

	const previousIssueKey = usePreviousIssueKey() ?? toIssueKey('');

	const previousIssueKeyRef = useRef(previousIssueKey);
	previousIssueKeyRef.current = previousIssueKey;

	const handlePrevious = useCallback(() => {
		const { onIssueChangeEvent } = propsRef.current;
		if (selectPreviousIssue(true)) {
			onIssueChangeEvent && onIssueChangeEvent(previousIssueKeyRef.current);
		}
		// Dependencies must be stable as downstream useMemo has an empty dependencies array
	}, [propsRef, selectPreviousIssue]);

	const openIssueFullScreenMessage = (() => {
		if (workType && expVal('jira-itr-m4-exp-3', 'isEnabled', false)) {
			return formatMessage(messages.openIssueFullScreenIssueTerminologyRefreshM4, {
				workType,
			});
		}

		return formatMessage(
			expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
				? messages.openIssueFullScreenIssueTermRefresh
				: messages.openIssueFullScreen,
		);
	})();

	return (
		<Shortcuts
			keyMap={useMemo(
				() => ({
					j: {
						callback: () => {
							const { disableListControls } = propsRef.current;

							if (disableListControls === true) {
								return;
							}

							// handleNext needs to be a stable function references as useMemo has an empty dependencies array
							handleNext();
						},
						label: (
							<div>
								{formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.nextIssueIssueTermRefresh
										: messages.nextIssue,
								)}
							</div>
						),
					},
					k: {
						callback: () => {
							const { disableListControls } = propsRef.current;

							if (disableListControls === true) {
								return;
							}

							// handlePrevious needs to be a stable function references as useMemo has an empty dependencies array
							handlePrevious();
						},
						label: (
							<div>
								{formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.previousIssueIssueTermRefresh
										: messages.previousIssue,
								)}
							</div>
						),
					},
					y: {
						callback: () => {
							const [{ isOpen }, { open, close }] = openStateRef.current;
							isOpen ? close() : open();
						},
						label: <div>{formatMessage(messages.displaySortFields)}</div>,
					},
					z: {
						callback: () => {
							if (selectedIssueKeyRef.current) {
								propsRef.current.onNavigateToIssue(selectedIssueKeyRef.current);
							}
						},
						label: <div>{openIssueFullScreenMessage}</div>,
					},
					arrowup: {
						callback: () => {
							const { disableListControls, focusedIssueKey } = propsRef.current;

							if (disableListControls === true) {
								return;
							}

							const isSelectedIssueFocused =
								focusedIssueKey !== null && focusedIssueKey === selectedIssueKeyRef.current;
							if (isSelectedIssueFocused) {
								handlePrevious();
							}
						},
						label: (
							<div>
								{formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.previousIssueIssueTermRefresh
										: messages.previousIssue,
								)}
							</div>
						),
					},
					arrowdown: {
						callback: () => {
							const { disableListControls, focusedIssueKey } = propsRef.current;

							if (disableListControls === true) {
								return;
							}

							const isSelectedIssueFocused =
								focusedIssueKey !== null && focusedIssueKey === selectedIssueKeyRef.current;
							if (isSelectedIssueFocused) {
								handleNext();
							}
						},
						label: (
							<div>
								{formatMessage(
									expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
										? messages.nextIssueIssueTermRefresh
										: messages.nextIssue,
								)}
							</div>
						),
					},
				}),
				[], // eslint-disable-line react-hooks/exhaustive-deps
			)}
		/>
	);
};

KeyboardShortcuts.defaultProps = {
	disableListControls: false,
};

export default KeyboardShortcuts;
